import { faBus, faCar, faMotorcycle, faPerson, faTruck } from '@fortawesome/free-solid-svg-icons'
import { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { ANPREvent } from 'domain/anpr-event'
import { useAuth } from 'layout/context/auth-context'
import { useLoading } from 'layout/context/loading-context'
import { Card } from 'primereact/card'
import { DataView } from 'primereact/dataview'
import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import CategoryBreakdownChips from 'shared/components/category-breakdown-chips'
import { DateFilterState } from 'shared/date-filter/date-filter-component'
import { getBrazilDate } from 'shared/utils/get-brazil-date'
import { useANPREvent } from '../hooks/useANPREvents'
import { emptyANPREvent } from '../types/consts'
import { exportANPREventsToCSV } from '../util/export-csv'
import { isSpeedViolation } from '../util/speed-violation-filter'
import { buildVehicleTypeBreakdown } from '../util/vehicle-type-breakdown'
import ANPREventGridItem from './anpr-event-grid-item'
import ANPREventListItem from './anpr-event-list-item'
import ANPREventsFilters from './anpr-event-filters'
import './anpr-event-view.scss'
import EventDetailViewer from './anpr-event-detail-dialog'
import EventImageViewer from './anpr-event-image-dialog'

const VEHICLE_TYPE_ICONS: Record<string, IconDefinition> = {
  cars: faCar,
  bikes: faMotorcycle,
  trucks: faTruck,
  buses: faBus,
  people: faPerson,
}

const blankDateFilter: DateFilterState = {
  preset: 'today',
  endDate: getBrazilDate('end'),
  startDate: getBrazilDate('start'),
}

const ANPREventsView = () => {
  const [searchParams] = useSearchParams()
  const [dateFilter, setDateFilter] = useState<DateFilterState>(blankDateFilter)
  const [datePreset, setDatePreset] = useState<string>('today')
  const [anprEvent, setANPREvent] = useState<ANPREvent>(emptyANPREvent)
  const [selectedImage, setSelectedImage] = useState<string | null>(null)
  const [anprEventDialog, setANPREventDialog] = useState(false)
  const [globalFilterValue, setGlobalFilterValue] = useState<string>('')
  const [colorFilter, setColorFilter] = useState<string>('')
  const [vehicleTypeFilter, setVehicleTypeFilter] = useState<string>('')
  // Pré-preenchido por quem chega aqui via "Ver eventos" no mapa (?camera=...).
  const [cameraNameFilter, setCameraNameFilter] = useState<string>(
    () => searchParams.get('camera') ?? ''
  )
  const [layout, setLayout] = useState<'grid' | 'list'>('grid')
  const [searchButtonHighlight, setSearchButtonHighlight] = useState(false)
  const [speedFilter, setSpeedFilter] = useState<number | null>(null)
  const [showOnlyViolations, setShowOnlyViolations] = useState(false)
  const { clientId } = useAuth()
  const { isLoading } = useLoading()
  const { anprEvents, fetchANPREvents } = useANPREvent()

  useEffect(() => {
    if (clientId) {
      fetchANPREvents({ clientId, startDate: dateFilter.startDate, endDate: dateFilter.endDate })
    }
  }, [clientId])

  const applyDatePreset = (preset: string) => {
    const now = new Date()
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())

    switch (preset) {
      case 'today':
        setDateFilter({ startDate: new Date(today.setHours(0, 0, 0, 0)), endDate: new Date(now.setHours(23, 59, 59, 999)), preset: 'today' })
        break
      case 'yesterday': {
        const yesterday = new Date(today)
        yesterday.setDate(yesterday.getDate() - 1)
        setDateFilter({ startDate: new Date(yesterday.setHours(0, 0, 0, 0)), endDate: new Date(yesterday.setHours(23, 59, 59, 999)), preset: 'yesterday' })
        break
      }
      case 'last7days': {
        const last7 = new Date(today)
        last7.setDate(last7.getDate() - 7)
        setDateFilter({ startDate: new Date(last7.setHours(0, 0, 0, 0)), endDate: new Date(now.setHours(23, 59, 59, 999)), preset: 'week' })
        break
      }
      case 'last30days': {
        const last30 = new Date(today)
        last30.setDate(last30.getDate() - 30)
        setDateFilter({ startDate: new Date(last30.setHours(0, 0, 0, 0)), endDate: new Date(now.setHours(23, 59, 59, 999)), preset: '2 weeks' })
        break
      }
      case 'custom':
        setDateFilter((prev) => ({ ...prev, preset: 'custom' }))
        break
    }
    setDatePreset(preset)
    setSearchButtonHighlight(true)
  }

  const handleSearch = () => {
    if (clientId) {
      fetchANPREvents({ clientId, startDate: dateFilter.startDate, endDate: dateFilter.endDate })
      setSearchButtonHighlight(false)
    }
  }

  const handleDateFilterChange = (prop: 'startDate' | 'endDate', value: any) => {
    setDateFilter((prev) => ({ ...prev, [prop]: new Date(value) }))
    setSearchButtonHighlight(true)
  }

  const viewEventDetails = (event: ANPREvent) => {
    setANPREvent({ ...event })
    setANPREventDialog(true)
  }

  const exportEvents = () => exportANPREventsToCSV(filteredEvents)

  const uniqueCameraNames = useMemo(
    () =>
      Array.from(new Set(anprEvents.map((e) => e.cameraName).filter(Boolean))).map((name) => ({
        label: name,
        value: name,
      })),
    [anprEvents]
  )

  const filteredEvents = useMemo(
    () =>
      anprEvents.filter((event) => {
        if (globalFilterValue && !event.licensePlate?.toLowerCase().includes(globalFilterValue.toLowerCase())) return false
        if (colorFilter && event.vehicleColor?.toLowerCase() !== colorFilter.toLowerCase()) return false
        if (vehicleTypeFilter && event.vehicleType?.toLowerCase() !== vehicleTypeFilter.toLowerCase()) return false
        if (cameraNameFilter && event.cameraName !== cameraNameFilter) return false
        if (speedFilter !== null && event.speed < speedFilter) return false
        if (showOnlyViolations && !isSpeedViolation(event)) return false
        return true
      }),
    [anprEvents, globalFilterValue, colorFilter, vehicleTypeFilter, cameraNameFilter, speedFilter, showOnlyViolations]
  )

  const vehicleTypeBreakdown = useMemo(
    () =>
      buildVehicleTypeBreakdown(filteredEvents).map((entry) => ({
        label: entry.label,
        count: entry.count,
        icon: VEHICLE_TYPE_ICONS[entry.type],
      })),
    [filteredEvents]
  )

  const itemTemplate = (event: ANPREvent) => {
    if (!event) return null
    if (layout === 'grid') {
      return <ANPREventGridItem event={event} onViewDetails={viewEventDetails} onImageClick={setSelectedImage} onExport={exportEvents} />
    }
    return <ANPREventListItem event={event} onViewDetails={viewEventDetails} onImageClick={setSelectedImage} onExport={exportEvents} />
  }

  return (
    <div className='events-container'>
      <div className='events-header'>
        <div className='events-title-section'>
          <i className='pi pi-video events-main-icon' />
          <h1 className='events-title'>Monitoramento de Veículos</h1>
        </div>
        <div className='events-subtitle'>Eventos de classificação e reconhecimento veicular em tempo real</div>
      </div>

      <Card className='events-card'>
        <div className='events-toolbar'>
          <ANPREventsFilters
            layout={layout}
            clientId={clientId}
            isLoading={isLoading}
            dateFilter={dateFilter}
            datePreset={datePreset}
            colorFilter={colorFilter}
            speedFilter={speedFilter}
            cameraNameFilter={cameraNameFilter}
            globalFilterValue={globalFilterValue}
            vehicleTypeFilter={vehicleTypeFilter}
            uniqueCameraNames={uniqueCameraNames}
            filteredEventsCount={filteredEvents.length}
            searchButtonHighlight={searchButtonHighlight}
            onExport={exportEvents}
            onSearch={handleSearch}
            onLayoutChange={setLayout}
            onApplyDatePreset={applyDatePreset}
            showOnlyViolations={showOnlyViolations}
            onShowOnlyViolationsChange={setShowOnlyViolations}
            onSpeedFilterChange={setSpeedFilter}
            onColorFilterChange={setColorFilter}
            onDateFilterChange={handleDateFilterChange}
            onGlobalFilterChange={setGlobalFilterValue}
            onCameraNameFilterChange={setCameraNameFilter}
            onVehicleTypeFilterChange={setVehicleTypeFilter}
          />
        </div>

        <CategoryBreakdownChips entries={vehicleTypeBreakdown} />

        <DataView
          value={filteredEvents}
          layout={layout}
          itemTemplate={itemTemplate}
          paginator
          rows={12}
          loading={isLoading}
          emptyMessage='Nenhum evento ANPR encontrado para este cliente.'
          paginatorTemplate='FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown'
          currentPageReportTemplate='Mostrando {first} a {last} de {totalRecords} eventos'
          rowsPerPageOptions={[12, 24, 36, 48]}
        />

        <EventDetailViewer
          setSelectedImage={setSelectedImage}
          anprEvent={anprEvent}
          hideDialog={() => setANPREventDialog(false)}
          visible={anprEventDialog}
        />
        <EventImageViewer selectedImage={selectedImage} setSelectedImage={setSelectedImage} />
      </Card>
    </div>
  )
}

export default ANPREventsView